import { fetchCategoryByUrl } from "@/services/collection/categories";
import { ItemStatusUrlString } from "@/services/collection";
import { Layout } from "@/router/types";
import { handleItemEditationAccess } from "@/router/guards/handleItemEditationAccess";
import { type RouteRecordRaw } from "vue-router";
import { RouteName } from "@/router/routeNameEnum";
import { createAiLookupFormLink, createError404Link, createHomepageLink } from "@/router/linkFactories";
import { useRememberedCategory } from "@/composables/categories/useRememberedCategory";
import { SupportedCategoryIds } from "@/services/aiIdentification";
import { checkUserHasRight } from "@/router/guards/checkUserHasRight";
import { Right } from "@/services/user/userTypes";

export const routes: RouteRecordRaw[] = [
  {
    name: RouteName.ArticleDraft,
    path: "magazine/preview/:id(\\d+)-:url/",
    components: { mainContent: () => import("@/views/magazine/ArticleDetail.vue") },
  },
  {
    path: `portfolio/:itemStatus(${ItemStatusUrlString.Deleted}|${ItemStatusUrlString.InMyCollection}|${ItemStatusUrlString.OnMarketplace})?`,
    redirect: (to) => ({
      name: RouteName.Collection,
      params: {
        countryCode: to.params.countryCode,
        itemStatus: to.params.itemStatus || "",
      },
    }),
  },
  {
    path: "collection",
    children: [
      {
        path: "",
        redirect: (to) => ({ name: RouteName.Collection, params: { countryCode: to.params.countryCode, itemStatus: ItemStatusUrlString.InMyCollection } }),
      },
      {
        path: "item/new",
        redirect: (to) => ({ name: RouteName.CollectionItemNew, params: { countryCode: to.params.countryCode } }),
      },
      {
        path: "item/:id(\\d+)/:nameSeoSlug?",
        redirect: (to) => ({
          name: RouteName.MyItemDetail,
          params: { countryCode: to.params.countryCode, id: to.params.id, nameSeoSlug: to.params.nameSeoSlug },
        }),
      },
      {
        path: `:itemStatus(${ItemStatusUrlString.Deleted}|${ItemStatusUrlString.InMyCollection}|${ItemStatusUrlString.OnMarketplace})?`,
        redirect: (to) => ({
          name: RouteName.Collection,
          params: { countryCode: to.params.countryCode, itemStatus: to.params.itemStatus || ItemStatusUrlString.InMyCollection },
        }),
      },
    ],
  },
  {
    path: "my-items",
    children: [
      {
        path: "item",
        children: [
          {
            path: "new",
            name: RouteName.CollectionItemNew,
            components: { mainContent: () => import("@/views/my-items/item/new.vue") },
            meta: {
              public: true,
            },
          },
          {
            name: RouteName.MyItemDetail,
            path: ":id(\\d+)/:nameSeoSlug?",
            components: { mainContent: () => import("@/views/my-items/item/index.vue") },
            meta: {
              public: true, // Only to allow beforeEnter() execution even for not logged in users.
            },
            beforeEnter: handleItemEditationAccess,
          },
        ],
      },
      {
        path: "",
        redirect: (to) => ({ name: RouteName.Collection, params: { ...to.params, itemStatus: ItemStatusUrlString.InMyCollection } }),
      },
      {
        path: `:itemStatus(${ItemStatusUrlString.Deleted}|${ItemStatusUrlString.InMyCollection}|${ItemStatusUrlString.OnMarketplace}|${ItemStatusUrlString.Expired}|${ItemStatusUrlString.SoldOut})`,
        name: RouteName.Collection,
        props: true,
        components: { mainContent: () => import("@/views/my-items/index.vue") },
        meta: {
          preventScrollToTop: true,
        },
      },
    ],
  },
  {
    path: "dashboard",
    redirect: () => createHomepageLink(),
  },
  {
    path: "account",
    children: [
      {
        path: "settings",
        name: RouteName.AccountSettings,
        components: { mainContent: () => import("@/views/account/Settings.vue") },
      },
      {
        beforeEnter: checkUserHasRight(Right.YearInReview),
        path: "year-in-review",
        name: RouteName.YearInReview,
        components: { mainContent: () => import("@/views/account/YearInReview.vue") },
      },
    ],
  },
  {
    path: "leaderboard",
    name: RouteName.Leaderboard,
    components: { mainContent: () => import("@/views/Leaderboard.vue") },
    meta: {
      grayBackground: true,
    },
  },
  {
    path: "saved-items",
    name: RouteName.SavedItems,
    components: { mainContent: () => import("@/views/SavedItems.vue") },
  },
  {
    path: "messages",
    children: [
      {
        path: "email-unsubscription-successfull",
        name: RouteName.MessagesEmailNotificationsUnsubscribed,
        components: { mainContent: () => import("@/views/messages/EmailUnsubscriptionSuccessfull.vue") },
        meta: {
          public: true,
        },
      },
      {
        path: "email-unsubscription-failed",
        name: RouteName.MessagesEmailNotificationsUnsubscribeError,
        components: { mainContent: () => import("@/views/messages/EmailUnsubscriptionFailed.vue") },
        meta: {
          public: true,
        },
      },
      {
        path: ":buddyId(\\d+)?/:buddyNickname?",
        name: RouteName.Messages,
        components: { mainContent: () => import("@/views/messages/index.vue") },
      },
    ],
  },
  {
    path: "colectio-ai",
    children: [
      {
        path: "history",
        name: RouteName.AiHistory,
        components: { mainContent: () => import("@/views/ai/History.vue") },
        meta: {
          grayBackground: true,
        },
      },
    ],
  },
  {
    name: RouteName.Error404,
    path: "/:countryCode(cz|sk)/:pathMatch(.*)",
    components: { mainContent: () => import("@/views/error/NotFound404.vue") },
    meta: {
      public: true,
    },
  },
  {
    path: "login",
    components: { mainContent: () => import("@/views/auth/Login.vue") },
    name: RouteName.Login,
    meta: {
      public: true,
      layout: Layout.LockingState,
      redirectWhenLoggedIn: { name: RouteName.Homepage },
    },
  },
  {
    path: "sign-in",
    children: [
      {
        path: "",
        name: RouteName.Registration,
        components: { mainContent: () => import("@/views/auth/Registration.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
          redirectWhenLoggedIn: { name: RouteName.Homepage },
        },
      },
      {
        name: RouteName.RegistrationEmailVerified,
        path: "email-verification/:hash",
        components: { mainContent: () => import("@/views/auth/EmailVerified.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
        },
      },
    ],
  },
  {
    path: "password-reset",
    children: [
      {
        path: "",
        name: RouteName.PasswordRestore,
        components: { mainContent: () => import("@/views/auth/PasswordRestoreRequest.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
          redirectWhenLoggedIn: { name: RouteName.Homepage },
        },
      },
      {
        path: ":hash",
        name: RouteName.PasswordRestoreProcessing,
        components: { mainContent: () => import("@/views/auth/PasswordRestore.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
          redirectWhenLoggedIn: { name: RouteName.Homepage },
        },
      },
    ],
  },
  {
    name: RouteName.Homepage,
    path: "",
    components: { mainContent: () => import("@/views/Homepage.vue") },
    meta: {
      public: true,
    },
  },
  {
    path: ":landingPageCategory(lego-sets|vinyl-records|board-games|sports-cards)",
    redirect: (to) => ({
      name: RouteName.Marketplace,
      params: { countryCode: to.params.countryCode },
      query: {
        category: to.params.landingPageCategory,
      },
    }),
  },
  {
    name: RouteName.Gdpr,
    path: "privacy-policy",
    components: { mainContent: () => import("@/views/legal/PrivacyPolicy.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.Tac,
    path: "terms-and-conditions",
    components: { mainContent: () => import("@/views/legal/TermsAndConditions.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.Guide,
    path: "guide",
    components: { mainContent: () => import("@/views/Guide.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.Contacts,
    path: "contacts",
    components: { mainContent: () => import("@/views/Contacts.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.About,
    path: "about-us",
    components: { mainContent: () => import("@/views/AboutUs.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.WhatColectioOffers,
    path: "what-colectio-offers",
    components: { mainContent: () => import("@/views/WhatColectioOffers.vue") },
    meta: {
      public: true,
    },
  },
  {
    path: "news",
    children: [
      {
        name: RouteName.NewsArticle,
        path: ":slug",
        components: { mainContent: () => import("@/views/news/NewsDetail.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: "magazine",
    children: [
      {
        name: RouteName.Article,
        path: ":id(\\d+)-:url",
        components: { mainContent: () => import("@/views/magazine/ArticleDetail.vue") },
        meta: {
          public: true,
        },
      },
      {
        name: RouteName.Magazine,
        path: ":category?",
        components: { mainContent: () => import("@/views/magazine/index.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    name: RouteName.PublicItemDetail,
    path: "collector/:nickname/:slug/:itemId",
    components: { mainContent: () => import("@/views/PublicCollectionItem.vue") },
    meta: {
      public: true,
    },
  },
  {
    name: RouteName.Collector,
    path: "collector/:nickname",
    components: { mainContent: () => import("@/views/Collector.vue") },
    meta: {
      public: true,
      grayBackground: true,
    },
  },
  {
    path: "marketplace",
    children: [
      {
        path: "",
        name: RouteName.Marketplace,
        components: { mainContent: () => import("@/views/marketplace/index.vue") },
        meta: {
          public: true,
          preventScrollToTop: true,
        },
      },
      {
        path: "item",
        children: [
          {
            name: RouteName.MarketplaceItem,
            path: ":marketplaceItemId(\\d+)/:seoSlug",
            components: { mainContent: () => import("@/views/marketplace/Item.vue") },
            meta: {
              public: true,
            },
          },
          {
            name: RouteName.MarketplaceItemNew,
            path: "new/:collectionItemId(\\d+)",
            components: { mainContent: () => import("@/views/marketplace/item/new.vue") },
          },
          {
            name: RouteName.MarketplaceItemEdit,
            path: "edit/:collectionItemId(\\d+)",
            components: { mainContent: () => import("@/views/marketplace/item/edit.vue") },
          },
          {
            name: RouteName.MarketplaceItemSaved,
            path: "saved/:collectionItemId(\\d+)",
            components: { mainContent: () => import("@/views/marketplace/item/saved.vue") },
          },
        ],
      },
      {
        name: RouteName.ShoppingCart,
        path: "cart",
        components: { mainContent: () => import("@/views/marketplace/Cart.vue") },
        meta: {
          renderSimpleFooter: true,
        },
      },
      {
        path: "order",
        children: [
          {
            name: RouteName.MarketplaceOrderSuccess,
            path: "thank-you",
            components: { mainContent: () => import("@/views/marketplace/order/ThankYou.vue") },
            meta: {
              grayBackground: true,
            },
          },
          {
            name: RouteName.MarketplaceOrder,
            path: "seller/:sellerNickname",
            components: { mainContent: () => import("@/views/marketplace/order/index.vue") },
            meta: {
              renderSimpleFooter: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: "sales",
    children: [
      {
        path: "",
        name: RouteName.Sales,
        components: { mainContent: () => import("@/views/Sales.vue") },
      },
      {
        path: ":orderId(\\d+)",
        name: RouteName.Sale,
        components: { mainContent: () => import("@/views/Sale.vue") },
      },
    ],
  },
  {
    path: "negotiations",
    name: RouteName.Negotiations,
    components: { mainContent: () => import("@/views/Negotiations.vue") },
  },
  {
    path: "purchases",
    children: [
      {
        path: "",
        name: RouteName.Purchases,
        components: { mainContent: () => import("@/views/Purchases.vue") },
      },
      {
        path: ":orderId(\\d+)",
        name: RouteName.Purchase,
        components: { mainContent: () => import("@/views/Purchase.vue") },
      },
    ],
  },
  {
    name: RouteName.Collectors,
    path: "collectors",
    components: { mainContent: () => import("@/views/Collectors.vue") },
    meta: {
      public: true,
      grayBackground: true,
    },
  },
  {
    path: "database",
    children: [
      {
        path: "",
        name: RouteName.Catalogue,
        components: { mainContent: () => import("@/views/collectibles/index.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
      {
        name: RouteName.CatalogueCategory,
        path: ":categoryUrl",
        components: { mainContent: () => import("@/views/collectibles/Category.vue") },
        meta: {
          public: true,
          grayBackground: true,
          preventScrollToTop: true,
        },
      },
      {
        name: RouteName.CatalogueItem,
        path: ":categoryUrl/:itemId(\\d+)",
        components: { mainContent: () => import("@/views/collectibles/Collectible.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "ai-identification",
    children: [
      {
        path: "",
        name: RouteName.AiForm,
        components: { mainContent: () => import("@/views/ai/index.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
      /**
       * This route uses "beforeEnter" instead of "redirect".
       * Because we need "async" and "redirect" cannot be async.
       * "component" property is used here only because "beforeEnter",
       * it is not really used.
       */
      {
        path: "category/:seoSlug",
        name: RouteName.AiFormWithPreselectedCategory,
        beforeEnter: async (to, from, next) => {
          const slug = String(to.params.seoSlug);
          const category = await fetchCategoryByUrl(slug);
          if (category === undefined) {
            return next(createError404Link());
          } else {
            if (SupportedCategoryIds.includes(category.id)) {
              useRememberedCategory().rememberCategoryId(category.id);
            }
            return next(createAiLookupFormLink());
          }
        },
        meta: {
          public: true,
        },
        components: { mainContent: () => import("@/views/ai/index.vue") },
      },
      {
        path: "history/:itemId",
        name: RouteName.AiDetail,
        components: { mainContent: () => import("@/views/ai/Detail.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "colectio-ai",
    children: [
      {
        path: "",
        redirect: { name: RouteName.AiForm },
        meta: {
          public: true,
        },
      },
      {
        path: "category/:categorySeoSlug",
        redirect: (to) => ({
          name: RouteName.AiFormWithPreselectedCategory,
          params: {
            countryCode: to.params.countryCode,
            seoSlug: to.params.categorySeoSlug,
          },
        }),
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: "soutez",
    children: [
      {
        path: "",
        name: RouteName.ComicCon,
        components: { mainContent: () => import("@/views/ComicCon.vue") },
        meta: {
          public: true,
          hideFooter: true,
        },
      },
      {
        path: "pravidla",
        name: RouteName.ComicConTerms,
        components: { mainContent: () => import("@/views/legal/ComicConTerms.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: "support",
    children: [
      {
        path: "",
        name: RouteName.Support,
        components: { mainContent: () => import("@/views/support/index.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
];
